import React from 'react';
import {
  ActionIcon,
  DetailStack,
  HStack,
  ICON_CLOSE,
  ICON_PLUS,
  ICON_SWAP_HORIZONTAL,
  SimpleTable,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import { Contact } from '../../../providers/api/dtos';
import { AIAnnotationContactData } from '../../../providers/api/dtos/documentFile';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { normalizeAiContactDataToContact } from '../../utils/mappers';
import { highlightContactField } from '../../utils/highlightContactField';
import { ContactSuggestion } from '../../types';

export interface SuggestedContactTableRowProps extends React.AriaAttributes {
  sellerSuggestion: ContactSuggestion,
  padded?: boolean,
  onAdd?: (contact: Partial<Contact>) => Promise<void> | void,
  onCompare?: (updatedContact: Partial<Contact>, existingContact: Contact) => Promise<void> | void,
  onIgnore?: (suggestion: AIAnnotationContactData) => Promise<void> | void,
}

const SuggestedContactTableRow: React.FC<SuggestedContactTableRowProps> = ({
  sellerSuggestion,
  padded = false,
  onAdd,
  onCompare,
  onIgnore,
  ...rest
}) => {
  const { suggestion, contact } = sellerSuggestion;
  const isPartialMatch = contact !== undefined;
  const normalizedSuggestion = normalizeAiContactDataToContact(suggestion);
  return (
    <SimpleTable.Row
      aria-label="Riga di un venditore suggerito"
      padded={padded}
      {...rest}
    >
      <SimpleTable.Cell>
        <DetailStack
          label="Nome"
          color={highlightContactField(sellerSuggestion, 'name')}
        >
          {normalizedSuggestion.name || NO_VALUE_SYMBOL}
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell>
        <DetailStack
          label="Data di nascita"
          color={highlightContactField(sellerSuggestion, 'birthday')}
        >
          {normalizedSuggestion.birthday ? formatDate(new Date(normalizedSuggestion.birthday)) : NO_VALUE_SYMBOL}
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell>
        <DetailStack
          label="Luogo di nascita"
          color={highlightContactField(sellerSuggestion, 'birthplace')}
        >
          {normalizedSuggestion.birthplace || NO_VALUE_SYMBOL}
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell>
        <DetailStack
          label="Codice fiscale"
          color={highlightContactField(sellerSuggestion, 'fiscalCode')}
        >
          {normalizedSuggestion.fiscalCode || NO_VALUE_SYMBOL}
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell>
        <DetailStack
          label="Residenza"
          color={highlightContactField(sellerSuggestion, 'residence.normalizedAddress')}
        >
          {normalizedSuggestion.residence?.normalizedAddress || NO_VALUE_SYMBOL}
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell align="right">
        <HStack gap={75}>
          {!isPartialMatch && (
            <ActionIcon
              icon={{ path: ICON_PLUS }}
              label="Aggiungi"
              aria-label="Aggiungi venditore suggerito"
              onClick={() => onAdd?.(normalizedSuggestion)}
              size="S"
            />
          )}
          {isPartialMatch && (
            <ActionIcon
              icon={{ path: ICON_SWAP_HORIZONTAL }}
              label="Compara/Aggiorna"
              aria-label="Compara e aggiorna venditore suggerito"
              onClick={() => onCompare?.(normalizedSuggestion, contact)}
              size="S"
            />
          )}
          <ActionIcon
            label="Ignora"
            aria-label="Ignora venditore suggerito"
            onClick={() => onIgnore?.(suggestion)}
            icon={{ path: ICON_CLOSE }}
            size="S"
          />
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default SuggestedContactTableRow;
